import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArcButton } from "@/components/ArcButton";
import { PenLine } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      navigate("/editor");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col px-4 py-16 sm:px-6">
      <div className="mb-6 flex h-12 w-12 items-center justify-center self-start rounded-md" style={{ backgroundColor: "var(--zyfley-soft)" }}>
        <PenLine size={22} style={{ color: "var(--zyfley)" }} />
      </div>
      <h1 className="font-serif-arc text-3xl font-black tracking-tight">Become an Editor</h1>
      <p className="mt-2 text-sm" style={{ color: "var(--arc-text-2)" }}>
        Sign up to draft articles for The Archives. Everything you write is reviewed by an administrator before it goes live.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-5 rounded-md border p-6 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
        {error && <div data-testid="register-error" className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">{error}</div>}
        <div className="space-y-2"><Label>Display name</Label><Input data-testid="register-name" value={form.name} onChange={set("name")} placeholder="How you'll be credited" /></div>
        <div className="space-y-2"><Label>Email</Label><Input data-testid="register-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required /></div>
        <div className="space-y-2"><Label>Password</Label><Input data-testid="register-password" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" required minLength={6} /></div>
        <ArcButton variant="zyfley" type="submit" disabled={loading} data-testid="register-submit" className="w-full">
          {loading ? "Creating account…" : "Create Editor Account"}
        </ArcButton>
        <p className="text-center text-sm" style={{ color: "var(--arc-text-2)" }}>
          Already have an account? <Link to="/login" className="zyfley-accent hover:underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
